import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import StatusBadge from "./StatusBadge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  CheckCircle, WarningCircle, Copy, ArrowRight, CircleNotch,
  CloudArrowUp, Globe, ArrowsClockwise,
} from "@phosphor-icons/react";

// ─── Helpers ─────────────────────────────────────────────────────────────────
function copyText(t) {
  navigator.clipboard.writeText(t);
  toast.success("Copied");
}

function StepIndicator({ current, total }) {
  return (
    <div className="flex items-center gap-1.5 mb-6">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 flex-1 rounded-full transition-colors ${
            i + 1 < current
              ? "bg-primary"
              : i + 1 === current
              ? "bg-primary/80"
              : "bg-muted"
          }`}
        />
      ))}
    </div>
  );
}

function InfoRow({ label, value, copyable }) {
  return (
    <div className="flex items-start justify-between gap-2 py-1.5 border-b border-border last:border-0">
      <span className="text-xs text-muted-foreground w-32 shrink-0">{label}</span>
      <span className="text-xs font-mono flex-1 text-right break-all">{value || "—"}</span>
      {copyable && value && (
        <button onClick={() => copyText(value)} className="shrink-0 ml-1 hover:text-primary">
          <Copy size={12} />
        </button>
      )}
    </div>
  );
}

function SuccessItem({ text }) {
  return (
    <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
      <CheckCircle size={13} weight="fill" />
      <span>{text}</span>
    </div>
  );
}

// ─── CF Status badge mapping ──────────────────────────────────────────────────
export function CfStatusBadge({ status }) {
  if (!status || status === "not_onboarded") return (
    <StatusBadge variant="default">Not Added</StatusBadge>
  );
  const map = {
    zone_created: ["info", "Zone Created"],
    ns_updated: ["warning", "Awaiting NS Update"],
    dns_configured: ["success", "DNS Configured"],
    pending_activation: ["warning", "Pending Activation"],
    active: ["success", "Active"],
    failed: ["danger", "Error"],
  };
  const [v, label] = map[status] || ["default", status];
  return <StatusBadge variant={v}>{label}</StatusBadge>;
}

// ─── Main Modal ──────────────────────────────────────────────────────────────
export default function CloudflareOnboardingModal({ open, onClose, domain, onCompleted }) {
  const TOTAL_STEPS = 6;
  const [step, setStep] = useState(1);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [cfStatus, setCfStatus] = useState(null);
  const [zoneResult, setZoneResult] = useState(null);
  const [nsChoice, setNsChoice] = useState("auto"); // auto | manual
  const [nsResult, setNsResult] = useState(null);
  const [nsError, setNsError] = useState(null);
  const [ip, setIp] = useState("");
  const [cleanupMode, setCleanupMode] = useState("safe");
  const [proxied, setProxied] = useState(true);
  const [ttl, setTtl] = useState(1);
  const [dnsResult, setDnsResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");

  // Reset state when domain changes or modal opens
  useEffect(() => {
    if (open && domain) {
      setStep(1);
      setZoneResult(null);
      setNsResult(null);
      setNsError(null);
      setDnsResult(null);
      setIp("");
      setNsChoice("auto");
      setCleanupMode("safe");
      setProxied(true);
      setSelectedAccount("");
      // Load accounts + existing CF status in parallel
      Promise.all([
        api.get("/cloudflare/accounts"),
        api.get(`/cloudflare/domains/${domain}/status`),
      ]).then(([accRes, statusRes]) => {
        const accs = accRes.data.accounts || [];
        setAccounts(accs);
        const def = accs.find((a) => a.is_default) || accs[0];
        if (def) setSelectedAccount(def.id);
        const st = statusRes.data;
        setCfStatus(st);
        // If already has a zone, populate zone result for resume
        if (st.onboarded && st.zone_id) {
          setZoneResult({
            zone_id: st.zone_id,
            domain,
            zone_status: st.zone_status,
            assigned_nameservers: st.assigned_nameservers || [],
            reused: true,
          });
        }
      }).catch(() => {});
    }
  }, [open, domain]);

  const startLoading = (msg) => { setLoading(true); setLoadingMsg(msg); };
  const stopLoading = () => { setLoading(false); setLoadingMsg(""); };

  // ── Step 3: Create Zone ────────────────────────────────────────────────────
  const handleCreateZone = async () => {
    if (!selectedAccount) return toast.error("Select a Cloudflare account first");
    startLoading("Creating Cloudflare zone…");
    try {
      const { data } = await api.post("/cloudflare/zones/create", {
        domain,
        cloudflare_account_id: selectedAccount,
      });
      setZoneResult(data);
      setStep(4);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      stopLoading();
    }
  };

  // ── Step 4: Update NS ──────────────────────────────────────────────────────
  const handleUpdateNS = async () => {
    if (nsChoice === "manual") { setStep(5); return; }
    startLoading("Updating nameservers at Namecheap…");
    try {
      const { data } = await api.post(
        `/cloudflare/zones/${zoneResult.zone_id}/update-nameservers-at-namecheap`
      );
      setNsResult(data);
      setNsError(null);
      toast.success("Nameservers updated at Namecheap");
      setStep(5);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      setNsError(msg);
      // Don't auto-advance — let user decide: retry or proceed manually
    } finally {
      stopLoading();
    }
  };

  // ── Step 5: Configure DNS ──────────────────────────────────────────────────
  const handleConfigureDns = async () => {
    if (!ip.trim()) return toast.error("Enter an IP address");
    const ipv4re = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipv4re.test(ip.trim())) return toast.error("Enter a valid IPv4 address");
    startLoading("Configuring DNS records…");
    try {
      const { data } = await api.post(
        `/cloudflare/zones/${zoneResult.zone_id}/configure-apex-www`,
        { ip_address: ip.trim(), cleanup_mode: cleanupMode, proxied, ttl }
      );
      setDnsResult(data);
      toast.success("DNS records configured successfully");
      setStep(6); // summary step
      onCompleted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      stopLoading();
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      // ── Step 1: Confirm Domain ──────────────────────────────────────────
      case 1:
        return (
          <div data-testid="cf-step-1">
            <h3 className="font-display font-bold text-lg mb-1">Confirm Domain</h3>
            <p className="text-sm text-muted-foreground mb-5">
              Review the selected domain before starting the Cloudflare onboarding flow.
            </p>
            <div className="bg-muted/40 rounded-lg p-4 mb-5 border border-border">
              <InfoRow label="Domain" value={domain} copyable />
              <InfoRow label="Registrar" value="Namecheap" />
              <InfoRow
                label="CF Status"
                value={cfStatus?.onboarding_status || "not_onboarded"}
              />
              {cfStatus?.zone_id && (
                <InfoRow label="Zone ID" value={cfStatus.zone_id} copyable />
              )}
            </div>
            {cfStatus?.onboarded && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-md p-3 text-xs text-yellow-700 dark:text-yellow-400 mb-4">
                This domain already has a Cloudflare zone. Proceeding will reuse the existing zone and re-run any incomplete steps.
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} data-testid="cf-step1-next">
                Continue <ArrowRight size={15} className="ml-2" />
              </Button>
            </div>
          </div>
        );

      // ── Step 2: Select Account ──────────────────────────────────────────
      case 2:
        return (
          <div data-testid="cf-step-2">
            <h3 className="font-display font-bold text-lg mb-1">Select Cloudflare Account</h3>
            <p className="text-sm text-muted-foreground mb-5">
              Choose which Cloudflare account to add this domain to.
            </p>
            {accounts.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                <CloudArrowUp size={32} className="mx-auto mb-3 opacity-40" />
                No Cloudflare accounts connected yet.{" "}
                <a href="/cloudflare" className="text-primary underline">Add one in Settings</a>
              </div>
            ) : (
              <div className="space-y-3 mb-5">
                {accounts.map((a) => (
                  <label
                    key={a.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedAccount === a.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
                    }`}
                    data-testid={`cf-select-account-${a.id}`}
                  >
                    <input
                      type="radio"
                      name="cf_account"
                      value={a.id}
                      checked={selectedAccount === a.id}
                      onChange={() => setSelectedAccount(a.id)}
                      className="h-4 w-4"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{a.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{a.account_id}</div>
                    </div>
                    {a.is_default && <StatusBadge variant="info">Default</StatusBadge>}
                    <StatusBadge variant={a.status === "connected" ? "success" : "default"}>
                      {a.status === "connected" ? "Connected" : a.status}
                    </StatusBadge>
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!selectedAccount || accounts.length === 0}
                data-testid="cf-step2-next"
              >
                Continue <ArrowRight size={15} className="ml-2" />
              </Button>
            </div>
          </div>
        );

      // ── Step 3: Create Zone ─────────────────────────────────────────────
      case 3:
        return (
          <div data-testid="cf-step-3">
            <h3 className="font-display font-bold text-lg mb-1">Create Cloudflare Zone</h3>
            <p className="text-sm text-muted-foreground mb-5">
              Add <span className="font-mono text-foreground">{domain}</span> to Cloudflare using the free plan.
            </p>
            <div className="bg-muted/40 rounded-lg p-4 mb-5 border border-border">
              <InfoRow label="Domain" value={domain} />
              <InfoRow
                label="CF Account"
                value={accounts.find((a) => a.id === selectedAccount)?.name || selectedAccount}
              />
              <InfoRow label="Plan" value="Free (Full setup)" />
            </div>
            {loading ? (
              <div className="flex items-center justify-center gap-3 py-6 text-muted-foreground text-sm">
                <CircleNotch size={20} className="animate-spin" />
                {loadingMsg}
              </div>
            ) : (
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <Button onClick={handleCreateZone} data-testid="cf-create-zone-btn">
                  <CloudArrowUp size={15} className="mr-2" />
                  Create Zone
                </Button>
              </div>
            )}
          </div>
        );

      // ── Step 4: Update Nameservers ──────────────────────────────────────
      case 4:
        return (
          <div data-testid="cf-step-4">
            <h3 className="font-display font-bold text-lg mb-1">Update Nameservers</h3>
            <p className="text-sm text-muted-foreground mb-4">
              To activate the Cloudflare zone, the domain must use Cloudflare's nameservers.
            </p>

            {/* Zone Created Summary */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-md p-3 mb-4">
              <SuccessItem text={`Zone created — ID: ${zoneResult?.zone_id}`} />
              <SuccessItem text={`Status: ${zoneResult?.zone_status || "pending"}`} />
              {zoneResult?.reused && <SuccessItem text="Reused existing zone" />}
            </div>

            {/* Assigned NS */}
            <div className="bg-muted/40 rounded-lg p-3 mb-4 border border-border">
              <div className="overline mb-2">Assigned Nameservers</div>
              {(zoneResult?.assigned_nameservers || []).map((ns) => (
                <div key={ns} className="flex items-center justify-between py-1">
                  <span className="font-mono text-xs">{ns}</span>
                  <button onClick={() => copyText(ns)} className="hover:text-primary ml-2">
                    <Copy size={13} />
                  </button>
                </div>
              ))}
            </div>

            {/* Choice */}
            <div className="space-y-2 mb-5">
              <label
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                  nsChoice === "auto" ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
                }`}
                data-testid="cf-ns-auto-option"
              >
                <input type="radio" name="ns_choice" value="auto" checked={nsChoice === "auto"}
                  onChange={() => setNsChoice("auto")} className="h-4 w-4" />
                <div>
                  <div className="text-sm font-medium">Auto-update at Namecheap</div>
                  <div className="text-xs text-muted-foreground">NameOps will set nameservers via Namecheap API automatically</div>
                </div>
              </label>
              <label
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                  nsChoice === "manual" ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
                }`}
                data-testid="cf-ns-manual-option"
              >
                <input type="radio" name="ns_choice" value="manual" checked={nsChoice === "manual"}
                  onChange={() => setNsChoice("manual")} className="h-4 w-4" />
                <div>
                  <div className="text-sm font-medium">I'll update manually</div>
                  <div className="text-xs text-muted-foreground">Copy the nameservers above and update them yourself</div>
                </div>
              </label>
            </div>

            {loading ? (
              <div className="flex items-center justify-center gap-3 py-4 text-muted-foreground text-sm">
                <CircleNotch size={20} className="animate-spin" /> {loadingMsg}
              </div>
            ) : (
              <>
                {nsError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-md p-3 text-xs text-red-600 dark:text-red-400 mb-4" data-testid="cf-ns-error">
                    <div className="flex items-start gap-2">
                      <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0" />
                      <div>
                        <div className="font-semibold mb-1">Namecheap update failed</div>
                        <div className="mb-2">{nsError}</div>
                        {nsError.includes("not configured") && (
                          <div>
                            Your Namecheap API credentials may be missing.{" "}
                            <a href="/settings" className="underline font-medium" target="_blank">
                              Check Settings →
                            </a>{" "}
                            then retry below.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex justify-between gap-2">
                  <Button variant="outline" onClick={() => setStep(3)}>Back</Button>
                  <div className="flex gap-2">
                    {nsError && (
                      <Button variant="outline" onClick={handleUpdateNS} data-testid="cf-ns-retry-btn">
                        Retry
                      </Button>
                    )}
                    <Button
                      onClick={nsError ? () => { setNsChoice("manual"); setStep(5); } : handleUpdateNS}
                      data-testid="cf-step4-next"
                      variant={nsError ? "outline" : "default"}
                    >
                      {nsError
                        ? "Skip — I'll update manually"
                        : nsChoice === "auto"
                          ? "Update & Continue"
                          : "Skip — I'll do it manually"
                      }
                      <ArrowRight size={15} className="ml-2" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        );

      // ── Step 5: DNS Setup ───────────────────────────────────────────────
      case 5:
        return (
          <div data-testid="cf-step-5">
            <h3 className="font-display font-bold text-lg mb-1">Configure DNS Records</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Set up A records for <span className="font-mono text-foreground">{domain}</span>.
              Existing conflicting records will be removed first.
            </p>

            {nsResult?.updated && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-md p-2 mb-4">
                <SuccessItem text="Nameservers updated at Namecheap" />
              </div>
            )}

            <div className="space-y-4 mb-5">
              <div>
                <label className="overline block mb-1">IPv4 Address *</label>
                <Input
                  value={ip}
                  onChange={(e) => setIp(e.target.value)}
                  placeholder="e.g. 104.21.45.123"
                  className="font-mono"
                  data-testid="cf-ip-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Both <span className="font-mono">@</span> and <span className="font-mono">www</span> A records will point to this IP.
                </p>
              </div>

              {/* Cleanup mode */}
              <div>
                <label className="overline block mb-2">Cleanup Mode</label>
                <div className="space-y-2">
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer text-sm ${
                    cleanupMode === "safe" ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
                  }`} data-testid="cf-cleanup-safe">
                    <input type="radio" name="cleanup" value="safe" checked={cleanupMode === "safe"}
                      onChange={() => setCleanupMode("safe")} className="h-4 w-4 mt-0.5" />
                    <div>
                      <div className="font-medium">Safe cleanup (recommended)</div>
                      <div className="text-xs text-muted-foreground">Only removes conflicting A / AAAA / CNAME records for @ and www</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer text-sm ${
                    cleanupMode === "full" ? "border-destructive bg-destructive/5" : "border-border hover:bg-muted/40"
                  }`} data-testid="cf-cleanup-full">
                    <input type="radio" name="cleanup" value="full" checked={cleanupMode === "full"}
                      onChange={() => setCleanupMode("full")} className="h-4 w-4 mt-0.5" />
                    <div>
                      <div className="font-medium text-destructive">Full zone wipe</div>
                      <div className="text-xs text-muted-foreground">Deletes ALL DNS records (except NS/SOA) before creating new ones</div>
                    </div>
                  </label>
                </div>
                {cleanupMode === "full" && (
                  <div className="mt-2 bg-red-500/10 border border-red-500/30 rounded p-2 text-xs text-red-600 dark:text-red-400" data-testid="cf-full-wipe-warning">
                    Warning: This will delete all existing DNS records in the Cloudflare zone.
                  </div>
                )}
              </div>

              {/* Options */}
              <div className="flex gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={proxied} onChange={(e) => setProxied(e.target.checked)} className="h-4 w-4 rounded" data-testid="cf-proxied-toggle" />
                  Proxied (orange cloud)
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={ttl !== 1} onChange={(e) => setTtl(e.target.checked ? 300 : 1)} className="h-4 w-4 rounded" />
                  Manual TTL {ttl !== 1 && <span className="font-mono text-xs">{ttl}s</span>}
                </label>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center gap-3 py-4 text-muted-foreground text-sm">
                <CircleNotch size={20} className="animate-spin" /> {loadingMsg}
              </div>
            ) : (
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(4)}>Back</Button>
                <Button onClick={handleConfigureDns} data-testid="cf-configure-dns-btn">
                  Configure DNS <ArrowRight size={15} className="ml-2" />
                </Button>
              </div>
            )}
          </div>
        );

      // ── Step 6: Summary ─────────────────────────────────────────────────
      case 6:
        return (
          <div data-testid="cf-step-summary">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-10 w-10 rounded-full bg-green-500/20 grid place-items-center">
                <CheckCircle size={22} weight="fill" className="text-green-500" />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg">Onboarding Complete</h3>
                <p className="text-xs text-muted-foreground">{domain}</p>
              </div>
            </div>

            <div className="bg-muted/40 rounded-lg p-4 border border-border mb-4 space-y-1">
              <InfoRow label="Zone ID" value={zoneResult?.zone_id} copyable />
              <InfoRow label="Zone Status" value={zoneResult?.zone_status || "pending"} />
              {(zoneResult?.assigned_nameservers || []).map((ns, i) => (
                <InfoRow key={ns} label={`Nameserver ${i + 1}`} value={ns} copyable />
              ))}
            </div>

            <div className="space-y-1.5 mb-5">
              <SuccessItem text="Cloudflare zone created" />
              {nsResult?.updated && <SuccessItem text="Nameservers updated at Namecheap" />}
              {!nsResult?.updated && nsChoice === "auto" && !nsError && (
                <div className="flex items-center gap-2 text-xs text-yellow-600 dark:text-yellow-400">
                  <WarningCircle size={13} weight="fill" />
                  <span>Nameserver update status unknown — verify in Namecheap</span>
                </div>
              )}
              {((!nsResult?.updated && nsChoice === "manual") || nsError) && (
                <div className="flex items-center gap-2 text-xs text-yellow-600 dark:text-yellow-400">
                  <WarningCircle size={13} weight="fill" />
                  <span>
                    {nsError
                      ? `Nameserver update failed: ${nsError}`
                      : "Nameservers need manual update (see above)"}
                  </span>
                </div>
              )}
              {dnsResult && (
                <>
                  {dnsResult.deleted_count > 0 && (
                    <SuccessItem text={`${dnsResult.deleted_count} old record(s) removed`} />
                  )}
                  <SuccessItem text={`A record @ → ${ip}`} />
                  <SuccessItem text={`A record www → ${ip}`} />
                </>
              )}
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-md p-3 text-xs text-blue-600 dark:text-blue-400 mb-5">
              DNS propagation and Cloudflare zone activation can take up to 24 hours after nameservers are updated.
            </div>

            <Button className="w-full" onClick={onClose} data-testid="cf-onboard-done-btn">
              Done
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="cf-onboarding-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CloudArrowUp size={18} />
            Add to Cloudflare
          </DialogTitle>
        </DialogHeader>

        {step <= TOTAL_STEPS && (
          <StepIndicator current={step} total={TOTAL_STEPS} />
        )}

        {renderStep()}
      </DialogContent>
    </Dialog>
  );
}
